/**
 * Environment variable utility with SST Resource support
 * Provides a unified interface for accessing environment variables
 * from process.env or SST resources with proper fallbacks
 */

// Lazy import SST Resource to avoid build-time issues
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let Resource: any = null;
let resourceLoadAttempted = false;

function getSSTResource() {
	if (!resourceLoadAttempted) {
		resourceLoadAttempted = true;
		try {
			// Only import SST in Node.js environment and when available
			if (typeof window === "undefined" && typeof process !== "undefined") {
				Resource = require("sst").Resource;
			}
		} catch (_error) {
			// SST not available, Resource remains null
			Resource = false; // Mark as unavailable to avoid repeated attempts
		}
	}
	return Resource || null;
}

/**
 * Check if we're in a static generation context where SST Resources might not be available
 */
function isStaticGenerationContext(): boolean {
	// During static generation, SST links might not be active
	// Check for common static generation indicators
	return (
		typeof process !== "undefined" &&
		process.env.NODE_ENV === "production" &&
		(process.env.NEXT_PHASE === "phase-production-build" || process.env.__NEXT_PRIVATE_PREBUNDLED_REACT === "next")
	);
}

/**
 * Get environment variable from SST Resource or process.env with intelligent fallback
 * @param key - Environment variable key
 * @param defaultValue - Default value if not found
 * @returns Environment variable value or default
 */
export function getEnv(key: string, defaultValue: string = ""): string {
	// In static generation context, prioritize process.env to avoid SST link issues
	if (isStaticGenerationContext()) {
		const envValue = process.env[key];
		if (envValue !== undefined) {
			return envValue;
		}
		return defaultValue;
	}

	// In runtime context, try SST Resource first (more reliable in deployment)
	if (typeof window === "undefined") {
		const resource = getSSTResource();
		if (resource) {
			try {
				const resourceValue = resource[key].value;
				if (resourceValue !== undefined && resourceValue !== null && resourceValue !== "") {
					return resourceValue;
				}
			} catch (_error) {
				// Resource access failed, continue to process.env fallback
			}
		}
	}

	// Fallback to process.env
	const envValue = process.env[key];
	if (envValue !== undefined) {
		return envValue;
	}

	// Return default value if nothing found
	return defaultValue;
}

/**
 * Get required environment variable (throws if not found)
 * @param key - Environment variable key
 * @returns Environment variable value
 * @throws Error if variable is not found
 */
export function getRequiredEnv(key: string): string {
	const value = getEnv(key);
	if (!value) {
		throw new Error(`Required environment variable ${key} is not set`);
	}
	return value;
}

/**
 * Check if environment variable exists
 * @param key - Environment variable key
 * @returns True if variable exists and is not empty
 */
export function hasEnv(key: string): boolean {
	const val = getEnv(key);
	return val.trim() !== "";
}

/**
 * Get environment variable as boolean
 * @param key - Environment variable key
 * @param defaultValue - Default boolean value
 * @returns Boolean value
 */
export function getEnvBoolean(key: string, defaultValue: boolean = false): boolean {
	const value = getEnv(key).toLowerCase();
	if (value === "true" || value === "1" || value === "yes") return true;
	if (value === "false" || value === "0" || value === "no") return false;
	return defaultValue;
}

/**
 * Get environment variable as number
 * @param key - Environment variable key
 * @param defaultValue - Default number value
 * @returns Number value
 */
export function getEnvNumber(key: string, defaultValue: number = 0): number {
	const value = getEnv(key);
	if (value === "") return defaultValue;
	const parsed = Number(value);
	return isNaN(parsed) ? defaultValue : parsed;
}
